import type { TFunction } from 'i18next';

/** Canonical permission module keys used in routes, nav, and RBAC checks. */
export const PERMISSION_MODULES = {
  DASHBOARD: 'dashboard',
  COMPANY: 'company',
  CONTACT: 'contact',
  INBOX: 'inbox',
  CONVERSATION: 'conversation',
  CAMPAIGN: 'campaign',
  CHANNEL: 'channel',
  TICKET: 'ticket',
  REPORT: 'report',
  CALL_REPORT: 'callreport',
  MESSAGE_REPORT: 'messagereport',
  CONVERSATION_REPORT: 'conversationreport',
  USER: 'user',
  TEAM: 'team',
  LABEL: 'label',
  TEMPLATE: 'template',
  CALL: 'call',
  ORGANIZATION: 'organization',
  BILLING: 'billing',
  WEBHOOK: 'webhook',
  CUSTOM_FIELDS: 'custom-fields',
  DISCOUNT: 'discount',
  SNIPPET: 'snippet',
  AUDIT_LOG: 'audit_log',
  LIFECYCLE: 'lifecycle',
  CHATBOT: 'chatbot',
  VOICEBOT: 'voicebot',
  WORKFLOW: 'workflow',
  AI_AGENTS: 'ai_agents',
  AI_ANALYTICS: 'ai_analytics',
  AI_SETTINGS: 'ai_settings',
  AI_USAGE: 'ai_usage',
  AUTO_ASSIGN: 'auto_assign',
  CALENDAR: 'calendar',
  ROLES: 'roles',
  LINE_TEMPLATE: 'linetemplate',
  CSAT_ANALYTICS: 'csat_analytics',
  CSAT_CONFIG: 'csat_config',
  TENANTS: 'tenants',
  NOTIFICATION: 'notification',
  SLA_POLICY: 'sla_policy',
  TASK: 'task',
  INTEGRATIONS: 'integrations',
} as const;

export const CORE_ACTIONS: string[] = ['view', 'create', 'edit', 'delete'];

export type CoreAction = 'view' | 'create' | 'edit' | 'delete';

/** Maps legacy or alternate module keys to canonical RBAC module keys. */
export const MODULE_ALIASES: Record<string, string> = {
  labels: 'label',
  account: 'company',
  role: 'roles',
  snippets: 'snippet',
  did: 'call',
  custom_field: 'custom-fields',
  workflows: 'workflow',
  tamplate: 'template',
  auditlog: 'audit_log',
  notifications: 'notification',
  'ai-settings': 'ai_settings',
  integration: 'integrations',
  // Task Statuses settings page uses the task permissions
  task_statuses: 'task',
};

/** i18n navbar keys for permission module display labels in role / allowlist UIs. */
export const MODULE_NAVBAR_KEYS: Record<string, string> = {
  ticket: 'TICKETS',
  webhook: 'WEBHOOK',
  billing: 'BILLING',
  dashboard: 'DASHBOARD',
  company: 'ACCOUNTS',
  account: 'ACCOUNTS',
  contact: 'CONTACTS',
  inbox: 'CHAT',
  conversation: 'CONVERSATION',
  campaign: 'CAMPAIGN',
  channel: 'CHANNEL',
  user: 'USERS',
  label: 'CUSTOM_LABELS',
  team: 'TEAMS',
  template: 'WHATSAPP_TEMPLATES',
  call: 'CALL_LOGS',
  userprofile: 'USER_PROFILE',
  organization: 'ORGANIZATION',
  notification: 'NOTIFICATIONS',
  balance: 'BILLING',
  whatsapp: 'VARIANCE_WHATSAPP_BUSINESS',
  email: 'VARIANCE_EMAIL',
  livechat: 'VARIANCE_LIVE_CHAT',
  voice: 'VARIANCE_VOICE',
  report: 'REPORT',
  conversationreport: 'CONVERSATION_REPORT',
  callreport: 'CALL_REPORT',
  messagereport: 'MESSAGE_REPORT',
  snippet: 'SNIPPETS',
  audit_log: 'AUDIT_LOGS',
  auditlog: 'AUDIT_LOGS',
  linetemplate: 'LINE_TEMPLATE',
  lifecycle: 'LIFECYCLE',
  chatbot: 'CHATBOT',
  workflow: 'WORKFLOWS',
  voicebot: 'VOICEBOT',
  ai_settings: 'AI_SETTINGS',
  ai_usage: 'AI_USAGE_BILLING',
  roles: 'ACCESS_CONTROL',
  'custom-fields': 'CUSTOM_FIELDS',
  custom_fields: 'CUSTOM_FIELDS',
  calendar: 'CALENDAR',
  sla_policy: 'SLA_POLICIES',
  task: 'TASKS',
};

const MODULE_DISPLAY_NAMES: Record<string, string> = {};

export function getModuleDisplayName(t: TFunction, moduleName: string): string {
  if (MODULE_DISPLAY_NAMES[moduleName]) {
    return MODULE_DISPLAY_NAMES[moduleName];
  }
  const key = moduleName.toLowerCase();
  const navbarKey = MODULE_NAVBAR_KEYS[key];
  if (navbarKey) {
    const translated = t(navbarKey);
    if (translated && translated !== navbarKey) {
      return translated;
    }
  }
  const capitalized = moduleName.charAt(0).toUpperCase() + moduleName.slice(1).replace(/_/g, ' ');
  return t(moduleName, { defaultValue: capitalized });
}
